import { createApp } from 'vue'

import { VueFire, VueFireAuthWithDependencies } from 'vuefire'
import { browserPopupRedirectResolver, indexedDBLocalPersistence } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { createHead } from '@unhead/vue/client'
import type { EmployerRole, PermissionLevel, PermissionResource } from '@swipe4work/models-orval'
import { VueQueryPlugin } from '@tanstack/vue-query'
import App from './App.vue'
import 'virtual:uno.css'

import type { UserModule } from './types'
import chatHubService from '@/modules/signalr-hubs/chat-hub'
import Vue3LottiePlugin from '@/modules/vue3lottie'
import type { AuthProvider } from '@/services/auth/auth-service'
import pinia from '@/modules/pinia'
import clickAsync from '@/directives/click-async'
import { router } from '@/router'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGE_SENDER_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
}

declare module 'vue-router' {
  interface RouteMeta {
    allowAnonymous?: boolean
    allowedRoles?: EmployerRole[]
    permissionResource?: PermissionResource
    permissionLevel?: PermissionLevel
    requiredAuthProvider?: AuthProvider
    shell?: {
      contentMode?: 'document' | 'workspace'
      showLeadPromotion?: boolean
    }

    layout?: string | boolean
  }
}

function init() {
  const app = createApp(App)

  // directives
  app.directive('click-async', clickAsync)

  app.use(chatHubService)

  app.use(createHead())
  app.use(Vue3LottiePlugin)

  app.use(VueFire, {
    // imported above but could also just be created here
    firebaseApp: initializeApp(firebaseConfig),
    modules: [
      VueFireAuthWithDependencies({
        dependencies: {
          popupRedirectResolver: browserPopupRedirectResolver,
          persistence: indexedDBLocalPersistence,
        },
      }),
    ],
  })

  app.use(router)

  app.use(pinia)

  app.use(VueQueryPlugin, {
    enableDevtoolsV6Plugin: true,
    queryClientConfig: {
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: (failureCount, error: any) => {
            if (error.response?.status === 500)
              return false

            return failureCount < 1
          },
        },
      },
    },
  })

  // Install modules in modules/ folder
  Object.values(import.meta.glob<{ install: UserModule }>('./modules/*.ts', { eager: true })).forEach(i => i.install?.(app, router))
  app.mount('#app')
}

// Track preload error reloads with a limit of 3
window.addEventListener('vite:preloadError', (error) => {
  console.info('Vite preload error:', error)

  // Get current reload count from localStorage
  const reloadCount = parseInt(localStorage.getItem('vitePreloadErrorReloadCount') || '0', 10)

  if (reloadCount < 3) {
    // Increment and save the reload count
    localStorage.setItem('vitePreloadErrorReloadCount', String(reloadCount + 1))
    window.location.reload()
  } else {
    console.error('Vite preload error persists after 3 reload attempts')
    // Reset the counter for future sessions
    localStorage.setItem('vitePreloadErrorReloadCount', '0')
  }
})

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', init)
else
  init()
